// ---------------------------------------------------------------------------
// SMART-structured IEP goal starter templates.
//
// These are *starter frames* only — teachers pick one that matches the
// domain they're writing for and then edit every field to match their
// specific student. Nothing here reproduces a proprietary curriculum;
// each template is a generic SMART frame you'd find in any teacher
// training text on IEP goal writing.
//
// Every template maps to the public.behavior_goals schema:
//
//   goalArea         — one of the domains AddGoalModal supports
//   specific         — what specifically will change
//   measurable       — how progress will be measured
//   baseline         — placeholder for the current level (must be edited)
//   target           — the target level of performance
//   masteryCriteria  — e.g. "3 consecutive weeks at 80%"
//
// The placeholders wrapped in [brackets] are prompts to the teacher —
// they are safe to save as-is in demo mode, but should always be edited
// before saving to a real student's record.
// ---------------------------------------------------------------------------

export const IEP_GOAL_TEMPLATES = [
  // Academic — reading
  {
    id: 'reading-comprehension',
    goalArea: 'academic',
    label: 'Reading comprehension (main idea)',
    description: 'Grade-level literal comprehension with visual supports.',
    specific:
      'Given a grade-level passage and up to 2 visual supports, the student will identify the main idea and 2 supporting details.',
    measurable:
      'Percent of comprehension questions answered correctly across 3 trials per week, recorded by the teacher.',
    baseline: '[Current baseline — e.g. "40% accuracy on baseline probes"]',
    target: '80% accuracy across 3 consecutive weekly probes',
    masteryCriteria: '3 consecutive weeks at 80% or higher across 2 settings',
  },
  {
    id: 'reading-decoding',
    goalArea: 'academic',
    label: 'Reading decoding (grade-level words)',
    description: 'Fluent decoding of grade-level words in isolation.',
    specific:
      'Given a list of grade-level words in isolation, the student will decode words accurately at their instructional reading level.',
    measurable:
      'Correct words per minute on a weekly 1-minute timed probe.',
    baseline: '[Current CWPM — e.g. "22 correct words per minute"]',
    target: '60 correct words per minute across 3 consecutive probes',
    masteryCriteria: '3 consecutive probes at target CWPM',
  },
  // Academic — math
  {
    id: 'math-computation',
    goalArea: 'academic',
    label: 'Math computation fluency',
    description: 'Single-digit + / − facts fluency.',
    specific:
      'Given a mixed set of grade-level math facts, the student will independently compute the correct answer.',
    measurable:
      'Percent correct on a 20-item weekly probe, timed to 2 minutes.',
    baseline: '[Current baseline — e.g. "50% correct in 2 minutes"]',
    target: '90% correct in 2 minutes across 3 consecutive probes',
    masteryCriteria: '3 consecutive weekly probes at target',
  },
  {
    id: 'math-word-problems',
    goalArea: 'academic',
    label: 'Math word problems (1-step)',
    description: 'Solving 1-step grade-level word problems.',
    specific:
      'Given a grade-level 1-step word problem with a graphic organizer, the student will identify the operation and solve.',
    measurable:
      'Percent correct across 5 word problems presented weekly.',
    baseline: '[Current baseline — e.g. "1/5 problems correct with support"]',
    target: '4 out of 5 problems correct across 3 consecutive weeks',
    masteryCriteria: '3 consecutive weeks at target',
  },
  // Academic — writing
  {
    id: 'writing-sentence',
    goalArea: 'academic',
    label: 'Written expression (complete sentences)',
    description: 'Writing complete sentences with a capital and period.',
    specific:
      'Given a picture prompt and a graphic organizer, the student will write 3 complete sentences with correct capitalization and end punctuation.',
    measurable:
      'Number of sentences with correct mechanics per weekly writing sample.',
    baseline: '[Current baseline — e.g. "1 of 3 sentences correct"]',
    target: '3 of 3 sentences with correct mechanics across 3 samples',
    masteryCriteria: '3 consecutive weekly writing samples at target',
  },
  // Behavioral
  {
    id: 'behavior-on-task',
    goalArea: 'behavioral',
    label: 'On-task behavior during instruction',
    description: 'Sustained engagement during small-group instruction.',
    specific:
      'During teacher-led small-group instruction, the student will remain on-task (eyes on task, appropriate materials, following directions) for the duration of the activity.',
    measurable:
      'Percent of 30-second intervals on-task during momentary time-sampling, recorded across 3 sessions per week.',
    baseline: '[Current baseline — e.g. "40% of intervals on-task"]',
    target: '85% of intervals on-task across 3 consecutive weeks',
    masteryCriteria: '3 consecutive weeks at target across 2 activities',
  },
  {
    id: 'behavior-frustration',
    goalArea: 'behavioral',
    label: 'Using a coping strategy when frustrated',
    description: 'Replacement behavior when work is hard.',
    specific:
      'When presented with a non-preferred or difficult task, the student will use a taught coping strategy (deep breaths, break card, or request help) instead of engaging in the target behavior.',
    measurable:
      'Percent of frustration episodes in which the student uses a coping strategy independently, recorded daily.',
    baseline: '[Current baseline — e.g. "0/5 episodes with coping strategy"]',
    target: '4 of 5 episodes with a coping strategy across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target across 2 activities',
  },
  {
    id: 'behavior-transitions',
    goalArea: 'behavioral',
    label: 'Transitioning between activities',
    description: 'Following the visual schedule to transition.',
    specific:
      'When given a transition warning and a visual schedule, the student will transition to the next activity within 2 minutes without adult prompting.',
    measurable:
      'Number of successful transitions per school day, recorded via daily point sheet.',
    baseline: '[Current baseline — e.g. "1 of 5 transitions independent"]',
    target: '4 of 5 daily transitions independent across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  {
    id: 'behavior-property-destruction',
    goalArea: 'behavioral',
    label: 'Keeping materials safe (property destruction)',
    description: 'Handling classroom materials appropriately instead of throwing or breaking them.',
    specific:
      'When given a non-preferred task with materials (e.g. iPad, worksheet, pencil), the student will keep the materials on the desk and use them appropriately for the duration of the task.',
    measurable:
      'Percent of tasks completed without throwing, hitting, or breaking materials. Baseline example: student throws his iPad 3 out of 5 times when asked to do math. Recorded across 5 daily tasks.',
    baseline: '[Current baseline — e.g. "Throws or damages materials on 3 of 5 tasks"]',
    target: '4 of 5 tasks without property destruction across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target across 2 activities',
  },
  {
    id: 'behavior-physical-aggression',
    goalArea: 'behavioral',
    label: 'Keeping hands & feet to self (physical aggression)',
    description: 'Replacement behavior for hitting, kicking, or pushing peers.',
    specific:
      'When frustrated by peers or adult direction, the student will use a taught replacement behavior (walk away, ask for a break, or tell an adult) instead of hitting, kicking, or pushing.',
    measurable:
      'Percent of frustration episodes without physical aggression. Recorded daily by the classroom teacher across all school routines.',
    baseline: '[Current baseline — e.g. "3 physical aggression episodes / day"]',
    target: '0 physical aggression episodes across 5 consecutive school days',
    masteryCriteria: '5 consecutive school days at target',
  },
  {
    id: 'behavior-elopement',
    goalArea: 'behavioral',
    label: 'Staying in the classroom (elopement)',
    description: 'Remaining in the classroom for the duration of instruction.',
    specific:
      'During academic instruction, the student will remain in the classroom (in seat or in an assigned area) until the end of the activity or until a break is granted.',
    measurable:
      'Number of instructional periods completed without eloping (leaving the room without permission). Recorded across all daily periods.',
    baseline: '[Current baseline — e.g. "Elopes during 3 of 6 daily periods"]',
    target: '6 of 6 daily periods without elopement across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  {
    id: 'behavior-verbal-aggression',
    goalArea: 'behavioral',
    label: 'Using kind words (verbal aggression)',
    description: 'Speaking respectfully to peers and adults instead of yelling, name-calling, or cursing.',
    specific:
      'When upset with a peer or adult, the student will use a respectful communication phrase (e.g. "I need space," "I don\'t like that," "please stop") instead of yelling, name-calling, or using inappropriate language.',
    measurable:
      'Percent of upset moments in which the student uses a respectful phrase, recorded daily.',
    baseline: '[Current baseline — e.g. "1 of 5 moments respectful"]',
    target: '4 of 5 upset moments respectful across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  {
    id: 'behavior-following-directions',
    goalArea: 'behavioral',
    label: 'Following adult directions (non-compliance)',
    description: 'Starting a directed task within a reasonable time.',
    specific:
      'When given a one-step adult direction, the student will begin the task within 30 seconds without arguing, refusing, or requiring more than one repetition.',
    measurable:
      'Percent of one-step directions followed within 30 seconds, sampled 5 times per school day.',
    baseline: '[Current baseline — e.g. "2 of 5 directions followed"]',
    target: '4 of 5 directions followed independently across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  {
    id: 'behavior-safe-body',
    goalArea: 'behavioral',
    label: 'Keeping body safe (self-injury)',
    description: 'Replacement behavior in place of self-injurious behavior.',
    specific:
      'When distressed or overstimulated, the student will use a taught replacement behavior (deep pressure, break card, or safe fidget) instead of engaging in self-injurious behavior.',
    measurable:
      'Number of self-injury episodes per school day, tracked continuously by classroom staff.',
    baseline: '[Current baseline — e.g. "4 SIB episodes / day"]',
    target: '0 SIB episodes across 5 consecutive school days',
    masteryCriteria: '5 consecutive school days at target',
  },
  // Social / Emotional
  {
    id: 'social-greeting',
    goalArea: 'social-emotional',
    label: 'Greeting peers',
    description: 'Initiating a greeting during arrival.',
    specific:
      'During arrival and structured social times, the student will initiate an appropriate greeting to a peer or adult.',
    measurable:
      'Number of independent greetings per structured social opportunity, recorded 3 times per week.',
    baseline: '[Current baseline — e.g. "1 of 5 opportunities"]',
    target: '4 of 5 opportunities across 3 consecutive weeks',
    masteryCriteria: '3 consecutive weeks at target',
  },
  {
    id: 'social-turn-taking',
    goalArea: 'social-emotional',
    label: 'Turn-taking in a small group',
    description: 'Following turn-taking rules during structured play.',
    specific:
      'During a structured turn-taking activity (game or academic partner work), the student will wait for their turn and respond appropriately when it is not their turn.',
    measurable:
      'Percent of turns taken appropriately, tallied across 3 sessions per week.',
    baseline: '[Current baseline — e.g. "50% of turns appropriate"]',
    target: '90% of turns appropriate across 3 consecutive weeks',
    masteryCriteria: '3 consecutive weeks at target',
  },
  // Communication
  {
    id: 'communication-requesting',
    goalArea: 'communication',
    label: 'Requesting a break (AAC / verbal)',
    description: 'Functional requesting during non-preferred tasks.',
    specific:
      'When presented with a non-preferred task, the student will request a break (verbally, using an AAC device, or with a break card) instead of engaging in the target behavior.',
    measurable:
      'Number of independent break requests per school day, recorded via daily data sheet.',
    baseline: '[Current baseline — e.g. "0 independent requests / day"]',
    target: '3 independent requests per day across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  {
    id: 'communication-answering',
    goalArea: 'communication',
    label: 'Answering wh- questions',
    description: 'Answering literal wh- questions about a text or picture.',
    specific:
      'Given a picture or short story, the student will accurately answer literal wh- questions (who, what, where, when).',
    measurable:
      'Percent of questions answered correctly across 5 questions per session, 3 times per week.',
    baseline: '[Current baseline — e.g. "40% accuracy"]',
    target: '80% accuracy across 3 consecutive weeks',
    masteryCriteria: '3 consecutive weeks at target across 2 stimulus sets',
  },
  // Functional / Life skills
  {
    id: 'functional-handwashing',
    goalArea: 'functional',
    label: 'Independent handwashing routine',
    description: 'Completing the full handwashing routine independently.',
    specific:
      'Given a picture-supported task analysis, the student will complete the handwashing routine (wet hands, soap, scrub 20 seconds, rinse, dry).',
    measurable:
      'Number of steps completed independently, recorded daily.',
    baseline: '[Current baseline — e.g. "2/5 steps independent"]',
    target: '5/5 steps independent across 3 consecutive days',
    masteryCriteria: '3 consecutive daily observations at target',
  },
  {
    id: 'functional-materials',
    goalArea: 'functional',
    label: 'Gathering materials for an activity',
    description: 'Following a list to gather materials.',
    specific:
      'Given a picture list, the student will independently gather the materials needed to start an activity.',
    measurable:
      'Percent of activities started with all correct materials, recorded daily.',
    baseline: '[Current baseline — e.g. "50% of activities"]',
    target: '90% of activities across 2 consecutive weeks',
    masteryCriteria: '2 consecutive weeks at target',
  },
  // Motor
  {
    id: 'motor-fine-writing',
    goalArea: 'motor',
    label: 'Fine motor — legible letter formation',
    description: 'Forming letters legibly with correct sizing and spacing.',
    specific:
      'Given lined paper and a writing prompt, the student will form letters with correct size, orientation, and spacing.',
    measurable:
      'Percent of letters formed correctly on a 20-letter weekly writing sample.',
    baseline: '[Current baseline — e.g. "40% of letters correct"]',
    target: '85% of letters correct across 3 consecutive samples',
    masteryCriteria: '3 consecutive weekly samples at target',
  },
  // Transition
  {
    id: 'transition-self-advocacy',
    goalArea: 'transition',
    label: 'Self-advocacy — requesting accommodations',
    description: 'Asking for a taught accommodation in class.',
    specific:
      'When the student encounters a task where a documented accommodation applies, they will request the accommodation from the teacher.',
    measurable:
      'Number of opportunities in which the student self-advocates, recorded weekly by the case manager.',
    baseline: '[Current baseline — e.g. "0/3 opportunities"]',
    target: '2/3 opportunities across 4 consecutive weeks',
    masteryCriteria: '4 consecutive weeks at target',
  },
];

// Helper — return templates for a specific goal_area, or all if 'all'/empty.
export function templatesForArea(goalArea) {
  if (!goalArea || goalArea === 'all') return IEP_GOAL_TEMPLATES;
  return IEP_GOAL_TEMPLATES.filter((t) => t.goalArea === goalArea);
}
